import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { TiresSvg } from '@components/TireOffer/TiresSvg';
import { plural, t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { QuoteStatus } from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiBasicCard, NoddiButton, NoddiDialog, NoddiIconButton, NoddiLink, useIsMobile } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { useState } from 'react';

interface TireSetOffersProps {
  userGroupId: number;
}

export const PendingTireOffers = ({ userGroupId }: TireSetOffersProps) => {
  const isMobile = useIsMobile();
  const [dialogOpen, setDialogOpen] = useState(false);
  const {
    isPending: isTireSetOffersPending,
    data: tireSetOffers,
    error: tireSetOffersError
  } = noddiAsync.useGet({
    type: URLKeys.getCarTireQuotesIgnoringImpersonation,
    input: { userGroupIds: [userGroupId], statuses: [QuoteStatus.PENDING] }
  });

  const {
    mutateAsync: postRejectTireOfferQuote,
    isPending: isPostRejectTireOfferQuotePending,
    error: postRejectTireOfferQuoteError
  } = noddiAsync.usePost({
    type: URLKeys.postRejectTireOfferQuote,
    queryConfig: {
      onSuccess: async () => {
        await invalidateQueryExactMatch({
          urlKey: URLKeys.getCarTireQuotesIgnoringImpersonation,
          input: { userGroupIds: [userGroupId] }
        });
        setDialogOpen(false);
      }
    }
  });

  if (isTireSetOffersPending) {
    return null;
  }

  if (tireSetOffersError || tireSetOffers.results.length === 0) {
    return null;
  }

  const hasMultipleOffers = tireSetOffers.results.length > 1;

  // todo : add this text back in when we when know they have tire offers that is created based on a lead.

  // const tirePluralMessage = plural(tireSetOffers.length, {
  //   one: 'Our measurements show that your tires may soon be, or are already, below the legal tread depth.',
  //   other:
  //     'Our measurements show that you have multiple tire sets where your tires may soon be, or are already, below the legal tread depth'
  // });

  const tirePluralMessage = plural(tireSetOffers.results.length, {
    one: `We've prepared a tire offer for your car, tailored to the right specifications for safe and comfortable driving`,
    other: `We've prepared tire offers for your cars, tailored to the right specifications for safe and comfortable driving`
  });

  const hasSelectedOrRejectedAllOffers = tireSetOffers.results.every((offer) => offer.isSelected || offer.isRejected);

  if (hasSelectedOrRejectedAllOffers) {
    return null;
  }

  return (
    <>
      <NoddiBasicCard variant='secondary' className='flex flex-col'>
        <div className='flex items-center justify-between gap-2 md:gap-4'>
          <p className='text-xl font-bold text-primary md:text-3xl'>
            <Trans>Offer for new tires</Trans>
          </p>
          <NoddiIconButton
            iconName='X'
            variant='ghost'
            onClick={() => {
              setDialogOpen(true);
            }}
          />
        </div>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-2'>
            <p>{tirePluralMessage}</p>
          </div>

          <div className='flex w-full items-end justify-between'>
            <div>
              <TiresSvg height={isMobile ? 50 : 98} width={isMobile ? 111 : 144} />
            </div>
            <NoddiLink
              to={
                hasMultipleOffers
                  ? customerRoutes.tireOfferHomePage.getPath()
                  : customerRoutes.tireOfferInfo.getPath({ slug: tireSetOffers.results[0]!.slug })
              }
              endIcon='ArrowRight'
            >
              <Trans>See more</Trans>
            </NoddiLink>
          </div>
        </div>
      </NoddiBasicCard>
      <NoddiDialog
        title={t`Are you sure you want to remove this offer?`}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        footer={
          <div className='flex flex-col gap-4'>
            <div className='flex justify-end gap-4'>
              <NoddiButton variant='outline' onClick={() => setDialogOpen(false)}>
                <Trans>Close</Trans>
              </NoddiButton>
              <NoddiButton
                loading={isPostRejectTireOfferQuotePending}
                // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs Promise.all(); React discards the returned promise, so a rejection is unhandled unless the body catches
                onClick={async () =>
                  await Promise.all(
                    tireSetOffers.results.map(
                      async (offer) =>
                        await postRejectTireOfferQuote({
                          carTireQuoteId: offer.id,
                          comments: 'User rejected offer from homepage'
                        })
                    )
                  )
                }
                variant='destructive'
              >
                <Trans>Yes, remove the offer</Trans>
              </NoddiButton>
            </div>
            {postRejectTireOfferQuoteError && <ApiErrorMessageWithTrans error={postRejectTireOfferQuoteError} />}
          </div>
        }
      >
        <p>
          <Trans>
            We'll remove the tire offer from your homepage. Remember, safe tires are essential for staying safe on the
            road.
          </Trans>
        </p>
      </NoddiDialog>
    </>
  );
};
